<template>
  <sp-card
    v-preserve-properties:elevation="cardElevation"
    :variant="cardVariant"
    class="entry-card"
    :class="modifierClasses"
    v-bind="cardAnalyticsAttributes"
    @click="handleClickOnCard"
  >
    <sp-card-content class="card-content">
      <sp-row v-preserve-properties:rowgap="0" align-content="space-between" height="100%" columngap="0" no-gutters>
        <SlotDayHeading :date="entry.date" :compact="isSmallView" :show-year="showYear" />

        <template v-if="isSmallView">
          <sp-button
            class="show-more"
            variant="text-inline"
            size="small"
            no-wrap
            :data-testid="TestId.slots.entrySummary"
            >{{ apptsLabel }}</sp-button
          >
        </template>

        <div v-else class="slots-container">
          <SlotButton
            v-for="(slot, index) in visibleSlots"
            :key="index"
            :appointment-slot="slot"
            :variant="slotVariant"
            analytics-element="slot-cards-slot"
            :analytics-value="listedDay"
          />

          <SlotMoreButton
            v-if="hasMoreSlots"
            class="more-button"
            :count="moreSlotsCount"
            :date="entry.date"
            :variant="moreButtonVariant"
            analytics-element="slot-cards-more-slots"
            @show-more="showMoreSlots"
          />
        </div>
      </sp-row>
    </sp-card-content>
  </sp-card>
</template>

<script setup>
import { vPreserveProperties } from "#ce/utils/property-bindings";
import { computed } from "vue";
import { i18n } from "#ce/components/SpFinder/i18n";
import { TestId } from "#/test-ids";
import SlotDayHeading from "#ce/components/SpFinder/slots/SlotDayHeading.ce.vue";
import SlotMoreButton from "#ce/components/SpFinder/slots/SlotMoreButton.ce.vue";
import SlotButton from "#ce/components/SpFinder/slots/SlotButton.ce.vue";
import { toDayKey } from "#ce/components/SpFinder/view-models/slot-format";

// Direct instance access (see SpSlotCards.ce.vue) — useI18n() can't resolve the
// plugin across the custom-element app boundary.
function t(...args) {
  return i18n.global.t(...args);
}

const emit = defineEmits(["select-slot", "show-more-slots"]);

const props = defineProps({
  /**
   * The entry object to display.
   *
   * @type {Object}
   * @property {String} date The date of the entry.
   * @property {Array} slots The slots for the entry.
   * @property {String} slots.start_time The start time of the slot.
   * @property {String} slots.booking_url The URL to book the slot.
   * @property {String} slots.visit_type_categories_description The description of the visit type.
   * @property {String[]} [slots.visit_type_categories] Visit types the slot serves — `in-person`, `video`.
   *
   * @example
   * {
   *  date: "2021-09-01",
   *  slots: [{
   *   start_time: "2021-09-01T09:00:00Z",
   *   booking_url: "https://example.com",
   *   visit_type_categories_description: "New Patient",
   *   visit_type_categories: ["video"]
   *  }],
   * }
   *
   * @required true
   */
  entry: {
    type: Object,
    required: true,
  },
  /**
   * The card's position among the cards, from 0.
   *
   * @type {Number}
   * @default 0
   */
  dayIndex: {
    type: Number,
    default: 0,
  },
  /**
   * Whether the card is being displayed on a mobile device.
   * We use the parent object to determine this in order to avoid having too many breakpoint listeners.
   *
   * @type {Boolean}
   * @default false
   */
  isSmallView: {
    type: Boolean,
    default: false,
  },
  /**
   * The maximum number of slots to display.
   *
   * This is only used when the card is being displayed on a non-mobile device.
   * Mobile devices will always display a summary.
   * The rest of the slots will be hidden and a "Show more" button will be displayed.
   *
   * @type {Number}
   * @default 2
   */
  maxSlots: {
    type: Number,
    default: 2,
  },
  slotVariant: {
    type: String,
    default: "outlined",
  },
  moreButtonVariant: {
    type: String,
    default: "outlined",
  },
  cardElevation: {
    type: Number,
    default: undefined,
    validator: (value) => [1, 2, 3].includes(value),
  },
  showYear: {
    type: String,
    default: "never",
    validator: (value) => ["never", "next_year", "always"].includes(value),
  },
});

const day = computed(() => toDayKey(props.entry.date));
const listedDay = computed(() => `day-${props.dayIndex + 1}`);

const cardVariant = computed(() => (props.isSmallView ? "outlined" : "elevated"));
const modifierClasses = computed(() => ({ "--is-small-view": props.isSmallView }));

const slots = computed(() => props.entry.slots);
const slotsCount = computed(() => slots.value.length);
const visibleSlotsCount = computed(() => (props.isSmallView ? 0 : props.maxSlots));
const visibleSlots = computed(() => slots.value.slice(0, visibleSlotsCount.value));
const moreSlotsCount = computed(() => slotsCount.value - visibleSlots.value.length);
const hasMoreSlots = computed(() => moreSlotsCount.value > 0);

const apptsLabel = computed(() => t("appts_count", { count: slotsCount.value }));

/**
 * In the small view the whole card opens the slot picker, so the card itself is the tracked control.
 */
const cardAnalyticsAttributes = computed(() => {
  const attributes = {};

  if (props.isSmallView) {
    attributes["data-analytics-element"] = "slot-cards-more-slots";
    attributes["data-analytics-interaction"] = "click";
    attributes["data-analytics-value"] = day.value;
  }

  return attributes;
});

function showMoreSlots() {
  emit("show-more-slots", { date: props.entry.date });
}

function handleClickOnCard() {
  if (props.isSmallView) {
    showMoreSlots();
  }
}
</script>

<style>
:host {
  /* Show more button */
  --show-more-button-position: var(--sp-comp-slot-cards-show-more-button-position, static);
  --show-more-button-bottom: var(--sp-comp-slot-cards-show-more-button-bottom, auto);
  --show-more-button-font-short: var(
    --sp-comp-slot-cards-show-more-button-font-short,
    var(--sp-sys-typescale-body-semi-bold-short)
  );
}
</style>

<style lang="scss" scoped>
.entry-card {
  --height-default: 16rem;
  --height-small: 8.5rem;
  --sp-comp-slot-cards-entry-card-padding-block: var(--sp-ref-scale-12) var(--sp-ref-scale-20);
  --sp-comp-slot-cards-entry-card-padding-inline: var(--sp-ref-scale-24);
  --sp-comp-card-padding-block: var(--sp-comp-slot-cards-entry-card-padding-block);
  --sp-comp-card-padding-inline: var(--sp-comp-slot-cards-entry-card-padding-inline);
  --sp-comp-card-border: var(--sp-comp-slot-cards-border, 1px solid var(--sp-sys-color-outline-variant));
  --sp-comp-card-border-radius: var(--sp-comp-slot-cards-border-radius, var(--sp-ref-border-radius-xs));
  --sp-card-root-height: var(--height-default);
  --sp-comp-card-content-height: var(--height-default);
  --sp-comp-slot-day-heading-title-font-short: var(--sp-comp-slot-cards-title-font-short);
  --sp-comp-slot-day-heading-title-font-short-compact: var(--sp-comp-slot-cards-title-font-short-mobile);
  --sp-comp-slot-day-heading-subtitle-font-short: var(--sp-comp-slot-cards-subtitle-font-short);
  --sp-comp-slot-more-button-font-weight: var(--sp-comp-slot-cards-more-button-font-weight);

  width: var(--sp-comp-slot-cards-card-width, 12rem);

  &[variant="outlined"] {
    --border-color: var(--sp-comp-slot-cards-outlined-border-color, var(--sp-sys-color-primary));
    --border-width: var(--sp-comp-slot-cards-outlined-border-width, 2px);
    --sp-comp-card-outlined-border: var(--border-width) solid var(--border-color);
    --sp-comp-card-content-height: var(--sp-comp-slot-cards-height, var(--height-default));
  }

  sp-button[variant="outlined"] {
    --sp-comp-button-small-padding: 0 var(--sp-ref-scale-4);
  }

  &.--is-small-view {
    --sp-comp-card-padding-block: var(--sp-comp-slot-cards-padding-block-mobile, var(--sp-ref-scale-16));
    --sp-comp-card-padding-inline: var(--sp-comp-slot-cards-padding-inline-mobile, var(--sp-ref-scale-16));
    --sp-comp-card-content-height: var(--sp-comp-slot-cards-height-small-view, var(--height-small));
    --sp-card-root-height: var(--height-small);

    width: 100%;
    min-width: 0;

    .show-more {
      position: var(--show-more-button-position);
      bottom: var(--show-more-button-bottom);
      --sp-comp-button-small-font-short: var(--show-more-button-font-short);
    }
  }
}

sp-card-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.slots-container {
  display: grid;
  grid-template-rows: repeat(3, var(--sp-comp-slot-cards-slot-height, var(--sp-ref-scale-36)));
  row-gap: var(--sp-comp-slot-cards-slot-row-gap, var(--sp-ref-scale-8));
  width: 100%;
  align-items: end;
}
</style>
